import { SupportRecordIdentities, type RecordIdentityOwner, type RecordIdentityProfile, type RecordIdentityReceipt } from './support-record-identity';
import { supportChecksPass, validBaselineCapture, type SupportStep } from './support-step';

type Configuration = RecordIdentityOwner & { profiles: RecordIdentityProfile[] };
const fail = (): never => { throw new Error('This workflow has no current record authority for this tenant.'); };
const normalized = (text: string) => text.replace(/\s+/g, ' ').trim();

type BaselineEntry = {
  owner: Readonly<RecordIdentityOwner>;
  record: Readonly<RecordIdentityReceipt>;
  selector: string;
  text: string;
  receipt: Readonly<{ key: string; token: string }>;
};

function visibleBaselineElement(element: Element): element is HTMLElement {
  if (!(element instanceof HTMLElement) || !element.isConnected || element.getClientRects().length === 0 ||
      element.closest('[hidden], [inert], [aria-hidden="true"], [data-floe-widget]')) return false;
  for (let ancestor: Element | null = element; ancestor; ancestor = ancestor.parentElement) {
    const style = getComputedStyle(ancestor);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' ||
        style.contentVisibility === 'hidden' || Number.parseFloat(style.opacity) === 0) return false;
  }
  return true;
}

function baselineText(element: HTMLElement): string | null {
  // Read the attached element's rendered innerText. Cloning into a detached
  // tree changes browser whitespace and line-break semantics.
  const text = normalized(element.innerText);
  return text && text.length <= 512 ? text : null;
}

/** API-authored policy arrives on a correlated lifecycle event, never an action argument. */
export class SupportRecordExecution {
  private registry: SupportRecordIdentities | null = null;
  private config: Configuration | null = null;
  private signature = '';
  private blockedRun: string | null = null;
  private receipts = new Map<string, Readonly<RecordIdentityReceipt>>();
  private baselineReceipts = new Map<string, Readonly<{ key: string; token: string }>>();
  private baselineKeys = new Set<string>();
  private baselines = new Map<string, BaselineEntry>();

  /** Validate and snapshot plan/run policy for this origin. A fail() or validation
   * error is caught: authority is retired and that run is blocked from retrying. */
  configure(runId: string, value: unknown): void {
    if (this.blockedRun === runId) return;
    try {
      const config = value as Configuration;
      if (!config || typeof config !== 'object' || Object.keys(config).length !== 4 ||
          config.runId !== runId || typeof config.planId !== 'string' || !config.planId || config.planId.length > 200 ||
          !Number.isInteger(config.planVersion) || config.planVersion < 1 ||
          !Array.isArray(config.profiles) || !config.profiles.length ||
          config.profiles.some(profile => profile?.origin !== location.origin)) return fail();
      const signature = JSON.stringify(config);
      if (this.config?.runId === runId) {
        if (this.signature !== signature) return fail();
        return;
      }
      const registry = new SupportRecordIdentities(config.profiles);
      if (!registry.activate(runId)) return fail();
      this.retire();
      this.registry = registry;
      this.config = JSON.parse(signature);
      this.signature = signature;
    } catch {
      this.retire();
      this.blockedRun = runId;
    }
  }

  /** Invalidate the registry and clear configuration and pending receipts,
   * preserving the blocked-run marker from a failed configuration. */
  retire(): void {
    this.registry?.retire();
    this.registry = null;
    this.config = null;
    this.signature = '';
    this.receipts.clear();
    this.baselineReceipts.clear();
    this.baselineKeys.clear();
    this.baselines.clear();
  }

  /** Return and delete an action's receipt; subsequent retrieval returns undefined. */
  takeReceipt(actionId: string): Readonly<RecordIdentityReceipt> | undefined {
    const receipt = this.receipts.get(actionId);
    this.receipts.delete(actionId);
    return receipt;
  }

  /** Return and delete a baseline's opaque receipt; subsequent retrieval returns undefined. */
  takeBaselineReceipt(actionId: string): Readonly<{ key: string; token: string }> | undefined {
    const receipt = this.baselineReceipts.get(actionId);
    this.baselineReceipts.delete(actionId);
    return receipt;
  }

  private baselineElements(selector: string, scopeSelector?: string): HTMLElement[] {
    const scope = scopeSelector
      ? [...document.querySelectorAll(scopeSelector)].filter(visibleBaselineElement)
      : [document.documentElement];
    if (scope.length !== 1) return [];
    const root = scope[0];
    const elements = [...root.querySelectorAll(selector)];
    if (root instanceof HTMLElement && root.matches(selector)) elements.unshift(root);
    return elements.filter(visibleBaselineElement);
  }

  private captureBaseline(
    owner: Readonly<RecordIdentityOwner>,
    record: Readonly<RecordIdentityReceipt>,
    resolved: Readonly<{ isCurrent: () => boolean; scopeSelector?: string }>,
    capture: { key: string; selector: string },
    actionId: string,
  ): void {
    if (!actionId || !validBaselineCapture(capture) || !resolved.isCurrent() ||
        this.baselineKeys.has(capture.key) || this.baselineKeys.size >= 16) return fail();
    // Reserve the key before observing the DOM. A failed attempt is still consumed
    // for this run, preventing retries from turning a baseline into a race.
    this.baselineKeys.add(capture.key);
    try {
      document.querySelectorAll(capture.selector);
      const elements = this.baselineElements(capture.selector, resolved.scopeSelector);
      if (elements.length !== 1) return fail();
      const text = baselineText(elements[0]);
      if (!text) return fail();
      const receipt = Object.freeze({key: capture.key, token: crypto.randomUUID()});
      this.baselines.set(capture.key, {
        owner: {...owner}, record: {...record}, selector: capture.selector,
        text, receipt,
      });
      this.baselineReceipts.set(actionId, receipt);
    } catch {
      return fail();
    }
  }

  private resolveBaseline(
    owner: Readonly<RecordIdentityOwner>,
    record: Readonly<RecordIdentityReceipt>,
    resolved: Readonly<{ isCurrent: () => boolean; scopeSelector?: string }>,
    reference: { key: string; token: string },
  ): BaselineEntry {
    const entry = this.baselines.get(reference.key);
    if (!entry || entry.receipt.token !== reference.token || entry.record.key !== record.key ||
        entry.record.profile !== record.profile || entry.record.token !== record.token ||
        entry.owner.runId !== owner.runId || entry.owner.planId !== owner.planId ||
        entry.owner.planVersion !== owner.planVersion || !resolved.isCurrent()) return fail();
    return entry;
  }

  private baselineMatches(entry: BaselineEntry, resolved: Readonly<{ isCurrent: () => boolean; scopeSelector?: string }>): boolean {
    if (!resolved.isCurrent()) return false;
    try {
      const elements = this.baselineElements(entry.selector, resolved.scopeSelector);
      return elements.length === 1 && baselineText(elements[0]) === entry.text;
    } catch { return false; }
  }

  prepare(step: SupportStep, runId?: string): {
    step: SupportStep;
    current: () => boolean;
    onRecord: () => boolean;
    baseline: () => boolean;
    capture: (actionId: string) => void;
    captureBaseline: (actionId: string) => void;
  } {
    const config = this.config;
    const registry = this.registry;
    const current = () => !!config && this.config === config && this.registry === registry &&
      config.runId === runId && config.planId === step.planId && config.planVersion === step.planVersion &&
      step.origin === location.origin && config.profiles.every(profile => profile.origin === location.origin);
    if (step.version !== 3 || !current() || !registry || !config) return fail();
    if (step.captureRecord && (step.action !== 'verify' || step.record || step.recordChecks)) return fail();
    const record = step.record ? registry.resolve(config, step.record) : null;
    if (step.recordChecks && !record) return fail();
    if (step.recordExit !== undefined && (step.recordExit !== true || step.action !== 'click' ||
        !record?.scopeSelector || step.skipIfComplete || !step.checks?.some(check =>
        check.kind === 'absent' && check.selector === record.scopeSelector))) return fail();
    if (step.captureBaseline && (!record || !step.record || !step.recordChecks || step.baseline)) return fail();
    const baselineEntry = step.baseline
      ? (record && step.record ? this.resolveBaseline(config, step.record, record, step.baseline) : fail())
      : null;
    const identityChecks = () => {
      if (!record) return true;
      const checks = step.recordChecks;
      if (!Array.isArray(checks) || checks.length !== 2 || checks.some(check =>
        !['text', 'value'].includes(check.kind) || Object.keys(check).some(key => !['kind', 'selector', 'text'].includes(key)))) return false;
      try {
        const elements = checks.map(check => [...document.querySelectorAll(check.selector)].filter(el =>
          el instanceof HTMLElement && el.getClientRects().length &&
          !el.closest('[hidden], [inert], [aria-hidden="true"], [data-floe-widget]') && getComputedStyle(el).visibility === 'visible'));
        return elements.every(group => group.length === 1) && elements[0][0] !== elements[1][0] &&
          !elements[0][0].contains(elements[1][0]) && !elements[1][0].contains(elements[0][0]) && supportChecksPass(checks, true);
      } catch { return false; }
    };
    const onRecord = () => current() && (!record || (record.matchesCurrentPage() && identityChecks()));
    let bound = step;
    if (record) {
      if (step.action === 'navigate' ? step.destination !== '{record}' :
        !Array.isArray(step.paths) || step.paths.length !== 1 || step.paths[0] !== '{record}') return fail();
      bound = {...step, paths: step.paths.map(path => path === '{record}' ? new URL(record.destination).pathname : path),
        ...(step.action === 'navigate' ? {destination: record.destination} : {})};
    }
    return {step: bound, current: () => current() && (!record || record.isCurrent()), onRecord,
      baseline: () => !baselineEntry || this.baselineMatches(baselineEntry, record!),
      capture: actionId => {
        if (!current() || !actionId || !step.captureRecord) return fail();
        const receipt = registry.capture(config, step.captureRecord);
        this.receipts.set(actionId, receipt);
      },
      captureBaseline: actionId => {
        if (!current() || !record || !step.record || !step.captureBaseline || !step.recordChecks) return fail();
        this.captureBaseline(config, step.record, record, step.captureBaseline, actionId);
      }};
  }
}
